import { Injectable } from "@angular/core";
import { collection, collectionData, doc, docData, Firestore } from "@angular/fire/firestore";
import { Store } from "@ngrx/store";
import { combineLatest, firstValueFrom, Observable, of, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { AppState } from "../app.state";
import { CryptoState, CryptoWallet, CryptoWalletWithoutNull, NftAsset } from "./crypto.state";
import * as cryptoActions from "./crypto.actions";
import { FireSubService } from "../../firebase/fireSub.service";
import { SOLANA_CHAIN_ID } from "../../firebase/crypto.service";

@Injectable({
    providedIn: 'root'
})
export class CryptoStateDispatcher {

    constructor(
        private store: Store<AppState>,
        private firestore: Firestore, 
        private fireSubService: FireSubService,
    ) {
        let favoriteSubscription: Subscription;
        let userId: string;
        let isAuthenticated: boolean;
        store.select('auth').pipe(
            tap(auth => {
                isAuthenticated = auth.isAuthenticated;
                if (auth.isAuthenticated) {
                    if (favoriteSubscription && !favoriteSubscription.closed) return;
                    favoriteSubscription = collectionData(collection(this.firestore, `users/${auth.userID}/favoriteNfts`)).pipe(distinctUntilChanged((x, y) => x.length === y.length)).subscribe(
                        (docmuents: NftAsset[]) => {
                            this.store.dispatch(cryptoActions.saveFavorites({favorites: docmuents}));
                        }
                    )
                    this.fireSubService.registerSubscription(favoriteSubscription);
                    userId = auth.userID;
                } else {
                    if (favoriteSubscription) favoriteSubscription.unsubscribe();
                    this.store.dispatch(cryptoActions.removeFavorites());
                }
            }),
            switchMap(() => {
                if (isAuthenticated) {
                    const metamask$ = docData(doc(this.firestore, `users/${userId}/wallets/metamask`)) as Observable<CryptoWallet>;
                    const phantom$ = docData(doc(this.firestore, `users/${userId}/wallets/phantom`)) as Observable<CryptoWallet>;
                    return combineLatest<[CryptoWallet | null, CryptoWallet | null]>([
                        metamask$,
                        phantom$,
                    ]);
                }
                return of<[null, null]>([null, null]);
            }),
            withLatestFrom(this.getState()),
            filter(([[metamask, phantom], state]) => {
                if (!metamask && !phantom) return false;

                const metamaskAddress = metamask?.address ? metamask.address : null;
                const phantomAddress = phantom?.address ? phantom.address : null;
                const stateAddress = state?.address ? state.address : null;

                return metamaskAddress !== stateAddress || phantomAddress !== stateAddress;
            }),
            map(([[metamask, phantom], _]) => {
                return { metamask, phantom };
            })
        ).subscribe(wallets => {
            if (wallets.metamask?.address === null && wallets.phantom?.address === null) {
                return;
            }

            let nonNullWallet: CryptoWalletWithoutNull;
            if (wallets.metamask?.address) nonNullWallet = wallets.metamask;
            else if (wallets.phantom?.address) nonNullWallet = wallets.phantom;

            if (nonNullWallet.chain == undefined) nonNullWallet.chain = wallets.metamask?.address ? 'ethereum' : 'solana';
            if (nonNullWallet.session == undefined) nonNullWallet.session = null; // migration
            if (nonNullWallet.connectedAt == undefined) nonNullWallet.connectedAt = null;

            this.store.dispatch(cryptoActions.loadWallet({...nonNullWallet}));
        })
    }

    public clearCryptoState() {
        this.store.dispatch(cryptoActions.clearCryptoState());
    }

    public getState(): Observable<CryptoState> {
        return this.store.select((state: AppState) => state.crypto).pipe(filter((cryptoState) => cryptoState != null));
    }

    public isLoggedIn(): Observable<CryptoIsLoggedInType> {
        return this.getState().pipe(
            map<CryptoState, CryptoIsLoggedInType>(cryptoState => {
                if (cryptoState.address !== null) {
                    return cryptoState.chainId === SOLANA_CHAIN_ID ? 'solana' : 'evm';
                }
                return null
            }),
            distinctUntilChanged()
        )
    }

    getNftAssets() {
        return this.getState().pipe(
            map(cryptoState => cryptoState.nftAssets)
        )
    }

    public getAddress(): Observable<string> {
        return this.getState().pipe(
            map(cryptoState => cryptoState.address)
        ) as Observable<string>
    }

    public getChainId(): Observable<string> {
        return this.getState().pipe(
            map(cryptoState => cryptoState.chainId)
        ) as Observable<string>
    }
}

export type CryptoIsLoggedInType = 'solana' | 'evm' | null;

